import { isSystemMessage, MessageType, ResponseMessage } from '@components/chat/types'
import { throttleQueue } from '@components/utils/throttleQueue'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import mime from 'mime'
import { Dispatch, SetStateAction } from 'react'

export type Filters = {
  strategy: string
  language: string
  extension_filter?: Array<string>
}

type Props = {
  query: string
  files?: Array<{ type: string; file_id: string }>
  setMessages: Dispatch<SetStateAction<Array<MessageType>>>
  onCreatedChat?: (chatId: string) => void
  chat?: string
  setLoader: Dispatch<SetStateAction<boolean>>
  filters: Filters
}

const updateMessage = (
  newMessage: ResponseMessage,
  set: Dispatch<SetStateAction<Array<MessageType>>>,
  onFinish: () => void,
  isError = false
) => {
  if (isError) {
    onFinish?.()
    set([
      {
        type: 'system',
        documents: newMessage.documents ?? [],
        answer: newMessage.answer,
        order: 0,
        created_at: new Date().toISOString(),
      },
    ])
    return
  }
  try {
    if (newMessage.event === 'workflow_started') {
      console.log('[updateMessage] workflow_started event detected')
      set((previous) => {
        const lastMessage = previous.at(-1)
        if (!isSystemMessage(lastMessage)) {
          return [
            ...previous,
            {
              type: 'system',
              documents: [],
              answer: '',
              order: previous.length,
              created_at: new Date().toISOString(),
            },
          ]
        }
        return previous
      })
    }
    if (newMessage.event === 'message') {
      set((previous) => {
        const lastMessage = previous.at(-1)
        if (!isSystemMessage(lastMessage)) {
          return previous
        }
        return [
          ...previous.slice(0, -1),
          {
            type: 'system',
            documents: newMessage.documents ?? lastMessage?.documents ?? [],
            answer: lastMessage.answer + newMessage.answer,
            order: lastMessage.order,
            created_at: lastMessage.created_at,
          },
        ]
      })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('error updating message', newMessage, error)
  }
}

const updateMessageThrottled = throttleQueue(updateMessage, 20)

export const extensions: Record<string, Array<string>> = {
  excel: ['.xls', '.xlsx'],
  word: ['.doc', '.docx'],
  pdf: ['.pdf'],
  email: ['.eml', '.msg'],
  text: ['.txt'],
}

export const sendQuerySSERequest = ({
  query,
  files,
  setMessages,
  chat,
  onCreatedChat,
  setLoader,
  filters,
}: Props): { controller: AbortController; taskIdRef: { current: string | null } } => {
  let hasOpened = false
  const controller = new AbortController()
  const taskIdRef = { current: null as string | null }
  let workflowFinished = false

  const handleFinish = () => {
    console.log('[handleFinish] Called, workflowFinished:', workflowFinished)
    if (!workflowFinished) {
      console.log('[handleFinish] Finishing workflow: stopping loader')
      workflowFinished = true
      setLoader(false)
      // Don't call controller.abort() here - let external code handle it
    } else {
      console.log('[handleFinish] Already finished, skipping')
    }
  }

  // Start the fetch operation but don't await it
  fetchEventSource(`/api/v2/chat-messages`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
    },
    method: 'POST',
    body: JSON.stringify({
      query: query,
      files,
      conversation_id: chat ?? undefined,
      inputs: {
        language: filters.language,
        // extension_filter: filters?.extension_filter?.flatMap((filter) => extensions?.[filter] || []) || [],
        // strategy: filters.strategy,
      },
      response_mode: 'streaming',
    }),
    signal: controller.signal,
    openWhenHidden: true,
    // Disable automatic retries
    async onopen(response) {
      console.log('[onopen] Response status:', response.status, 'Content-Type:', response.headers.get('content-type'))
      if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
        console.log('[onopen] Connection to SSE opened successfully')
        if (!hasOpened) {
          hasOpened = true
          setLoader(true)
          setMessages((previous) => [
            ...previous,
            { type: 'system', documents: [], answer: '', order: previous.length, created_at: new Date().toISOString() },
          ])
        }
      } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        // Client errors (except rate limiting) shouldn't retry
        throw new Error(`Client error: ${response.status}`)
      } else {
        throw new Error(`Failed to connect: ${response.status}`)
      }
    },
    onmessage: (event) => {
      if ('data' in event) {
        try {
          const newMessage: ResponseMessage = JSON.parse(event.data as string) as ResponseMessage
          const taskId = newMessage.task_id

          // Store task_id for potential stop API call
          if (taskId && !taskIdRef.current) {
            taskIdRef.current = taskId
            console.log('[onmessage] Stored task_id:', taskId)
          }

          if (newMessage.event === 'error') {
            console.error('[onmessage] Received error event:', newMessage)
            updateMessageThrottled(newMessage, setMessages, handleFinish, true)
            return
          }
          if (newMessage.event === 'node_finished') {
            if (newMessage.data?.outputs?.result) {
              const result = newMessage.data?.outputs?.result
              if (!Array.isArray(result) || result.length === 0 || !result[0]?.metadata?.document_name) {
                return
              }

              const results = Array.isArray(result) ? result : result ? [result] : []
              console.log('[updateMessage] Setting documents from results:', results.length)
              setMessages((previous) => {
                const lastMessage = previous.at(-1)
                if (!isSystemMessage(lastMessage)) {
                  return previous
                }
                const images = results
                  ?.filter((res) => res.metadata?.document_name.endsWith('.png'))
                  .map((img: any) => ({
                    position: img.position,
                    title: img.content,
                    url: `/images/${img.document_name}`,
                  }))

                const documents = results
                  .filter((doc) => doc?.metadata?.document_id)
                  .filter((res) => !res.metadata?.document_name.endsWith('.png'))
                  .map(({ metadata = {} }: { metadata: any }) => ({
                    file_id: metadata.document_id,
                    file_uri: `/api/v2/documents/${metadata.document_id}/download`,
                    filename: metadata.document_name,
                    relative_path: metadata.document_name,
                    metadata: {
                      filetype: mime.getType(metadata.document_name.split('.').pop() || ''),
                    },
                  }))
                  .filter((doc, index, arr) => {
                    return arr.findIndex((d) => d.file_id === doc.file_id) === index
                  })

                console.log('images', images, 'documents', documents)
                return [
                  ...previous.slice(0, -1),
                  {
                    type: 'system',
                    images: [...(lastMessage.images || []), ...(images || [])],
                    documents: [...(lastMessage.documents || []), ...documents],
                    answer: lastMessage.answer,
                    order: lastMessage.order,
                    created_at: lastMessage.created_at,
                  },
                ]
              })
            }
            return
          }

          // Update messages without calling handleFinish for regular events
          updateMessageThrottled(newMessage, setMessages, () => {})

          // Handle workflow_finished separately, outside of updateMessage
          if (newMessage.event === 'workflow_finished') {
            console.log('[onmessage] Detected workflow_finished')
            if (newMessage?.conversation_id) {
              console.log('[onmessage] Calling onCreatedChat with ID:', newMessage.conversation_id)
              onCreatedChat?.(newMessage.conversation_id)
            }
            // Give the throttled queue time to process, then finish
            handleFinish()
          }
        } catch (parseError) {
          console.error('[onmessage] Failed to parse event data:', parseError, 'Raw data:', event.data)
        }
        // if (newMessage.event === 'node_finished' && newMessage.data?.outputs.result) {
        //   console.log('RAG Result', newMessage)
        // }
      } else {
        console.log('[onmessage] Event has no data property')
      }
    },
    onerror: (err) => {
      console.error('[onerror] SSE Error occurred:', err)
      console.error('[onerror] Error type:', typeof err, 'Error details:', JSON.stringify(err, null, 2))
      handleFinish()
      throw err // This prevents further retries
    },
  }).catch((error) => {
    console.error('[fetchEventSource] Error:', error)
    handleFinish()
  })

  return { controller, taskIdRef }
  // eventSource.onopen = () => {
  //   setLoader(true)
  //   setMessages((previous) => [...previous, { type: 'system', documents: [], answer: '', order: previous.length }])
  // }

  // eventSource.onanswer = (event: Event) => {
  //   if ('data' in event) {
  //     const newMessage: ResponseMessage = JSON.parse(event.data as string) as ResponseMessage
  //     updateMessageThrottled(newMessage, setMessages)
  //     if (newMessage?.chat_id) {
  //       onCreatedChat?.(newMessage.chat_id)
  //     }
  //   }
  // }

  // eventSource.ondocument = (event: Event) => {
  //   if ('data' in event) {
  //     const newMessage: ResponseMessage = JSON.parse(event.data as string) as ResponseMessage
  //     updateMessageThrottled(newMessage, setMessages, () => setLoader(false), false)
  //     if (newMessage?.chat_id) {
  //       onCreatedChat?.(newMessage.chat_id)
  //     }
  //   }
  // }

  // eventSource.onerror = (event: Event) => {
  //   try {
  //     if (
  //       'data' in event &&
  //       ((typeof event.data === 'string' && event.data?.length > 0) || typeof event.data === 'object')
  //     ) {
  //       const newMessage: ResponseMessage = JSON.parse(event.data as string) as ResponseMessage
  //       const errorMessage: ResponseMessage = {
  //         system: {
  //           text: 'Error',
  //           total: newMessage.error || 'An error occurred',
  //           created_at: new Date().toISOString(),
  //           fragment_num: 0,
  //         },
  //         documents: [],
  //         chat_id: chat || 0,
  //       }
  //       updateMessageThrottled(errorMessage, setMessages, undefined, true)
  //       if (newMessage?.chat_id) {
  //         onCreatedChat?.(newMessage.chat_id)
  //       }
  //     } else {
  //       throw new Error('no response data')
  //     }
  //     // eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
  //   } catch (_error) {
  //     let errorMessage: ResponseMessage = {
  //       system: {
  //         text: 'Error',
  //         total: 'An error occurred',
  //         created_at: new Date().toISOString(),
  //         fragment_num: 0,
  //         type: 'system',
  //       },
  //       documents: [],
  //       chat_id: chat || 0,
  //     }
  //     if ('responseCode' in event && typeof event.responseCode === 'number') {
  //       switch (event.responseCode) {
  //         case 401:
  //           errorMessage.system!.total = 'Authorization failed'
  //           break
  //         case 403:
  //           errorMessage.system!.total = 'Chat does not belong to the user'
  //           break
  //         case 404:
  //           errorMessage.system!.total = 'Chat does not exist'
  //           break
  //         case 409:
  //           errorMessage.system!.total = 'Files are currently being indexed, chat is not available at this time'
  //           break
  //         case 422:
  //           errorMessage.system!.total = 'Validation Error'
  //           break
  //         default:
  //           errorMessage = {
  //             system: {
  //               text: 'Error',
  //               total: 'An error occurred',
  //               created_at: new Date().toISOString(),
  //               fragment_num: 0,
  //               type: 'system',
  //             },
  //             documents: [],
  //             chat_id: chat || 0,
  //           }
  //           break
  //       }
  //     } else {
  //       errorMessage = {
  //         system: {
  //           text: 'Error',
  //           total: 'An error occurred',
  //           created_at: new Date().toISOString(),
  //           fragment_num: 0,
  //           type: 'system',
  //         },
  //         documents: [],
  //         chat_id: chat || 0,
  //       }
  //     }
  //     updateMessageThrottled(errorMessage, setMessages, undefined, true)
  //   } finally {
  //     setLoader(false)
  //   }
  // }
  // eventSource.stream()
}
